import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import {PageForbiddenComponent} from "./authentication/page-forbidden/page-forbidden.component";
import {ResourcePlanerComponent} from "./scheduler/resouce-planer/resource-planer.component";
import {SchedulerResolver} from "./scheduler/scheduler.resolver";
import {DepartmentComponent} from "./setting/department/department.component";
import {DepartmentResolver} from "./setting/resolvers/department.resolver";
import {AuthGuard} from "./shared/guards/auth.guard";
import { TenantSettingGuard } from "./shared/guards/tenant-setting.guard";
import {HomeComponent} from "./home/home/home.component";
import { TenantComponent } from "./tenant/tenant/tenant.component";
import { TenantResolver } from "./tenant/tenant.resolver";
import { EmployeeResolver } from "./employee/resolvers/employee.resolver";
import { EmployeeComponent } from "./employee/employee/employee.component";
import { PageNotFoundComponent } from "./authentication/page-not-found/page-not-found.component";
import { LoginComponent } from "./authentication/login/login.component";
import { EmployeeRolesComponent } from "./setting/employee-roles/employee-roles.component";
import { EmployeeRoleResolver } from "./setting/resolvers/employee-role.resolver";
import { PermissionRolesComponent } from "./setting/permission-roles/permission-roles.component";
import { PremissionRolesResolver } from "./setting/resolvers/premission-roles.resolver";
import { ProfileComponent } from "./setting/profile/profile.component";
import { RessourceCategoryComponent } from "./setting/ressource-category/ressource-category.component";
import { RessourceCategoryResolver } from "./setting/resolvers/ressource-category.resolver";
import { RessourceResolver } from "./setting/resolvers/ressource.resolver";
import { RessourceComponent } from "./setting/ressource/ressource.component";
import { TimeEntryCategoryComponent } from "./time-tracking/time-entry-category/time-entry-category.component";
import { TimeEntryCategoriesResolver } from "./time-tracking/resolvers/time-entry-categories.resolver";
import { TimeEntriesComponent } from "./time-tracking/time-entries/time-entries.component";
import { TimeEntriesResolver } from "./time-tracking/resolvers/time-entries.resolver";
import { CurrentEmployeeResolver } from "./home/current-employee.resolver";
import { ContactsComponent } from "./contact/contacts/contacts.component";
import { ContactResolver } from "./contact/resolvers/contact.resolver";
import { ContactsResolver } from "./contact/resolvers/contacts.resolver";
import { ContactComponent } from "./contact/contact/contact.component";
import { ServicesResolver } from "./assignment/resolvers/services.resolver";
import { ServicesGridComponent } from "./assignment/services-grid/services-grid.component";
import { AssignmentsComponent } from "./assignment/assignments/assignments.component";
import { AssignmentsResolver } from "./assignment/resolvers/assignments.resolver";
import { AssignmentResolver } from "./assignment/resolvers/assignment.resolver";
import { AssignmentComponent } from "./assignment/assignment/assignment.component";
import { AssignmentTypeComponent } from "./assignment/assignment-type/assignment-type.component";
import { AssignmentTypesResolver } from "./assignment/resolvers/assignment-types.resolver";
import { HolidayGridComponent } from "./setting/holiday/holiday-grid/holiday-grid.component";
import { HolidayResolver } from "./setting/holiday/resolvers/holiday.resolver";
import { CurrentDayHolidayResolver } from "./setting/holiday/resolvers/current-day-holiday.resolver";
import { SettingComponent } from "./setting/setting/setting.component";
import { TenantSettingsComponent } from "./setting/tenant-settings/tenant-settings-ui/tenant-settings.component";
import { EmployeesComponent } from "./employee/employees/employees.component";
import { EmployeesResolver } from "./employee/resolvers/employees.resolver";
import { AbsencesComponent } from "./time-tracking/absences/absences.component";
import { FlexTimeCorrectionGridComponent } from "./setting/flex-time-correction/flex-time-correction-grid/flex-time-correction-grid.component";
import { FlexTimeCorrectionsResolver } from "./setting/flex-time-correction/resolvers/flex-time-corrections.resolver";
import { EmployeeWorkTimeResolver } from "./employee/resolvers/employee-work-time.resolver";
import { SchedulerResourcesResolver } from "./setting/resolvers/scheduler-resources.resolver";
import { FiscalYearGridComponent } from "./setting/fiscal-year/fiscal-year-grid/fiscal-year-grid.component";
import { TimeSupplementsComponent } from "./setting/time-supplement/time-supplements/time-supplements.component";
import { ExpensesComponent } from "./expenses/expenses/expenses.component";
import { ExpenseTypesComponent } from "./setting/expense-types/expense-types.component";

const routes: Routes = [
  {
    path: "",
    component: HomeComponent,
    data: { title: 'Dashboard', permission: 'Permissions.Dashboard.Frontend'},
    canActivate: [AuthGuard],
  },
  {
    path: "scheduler",
    component: ResourcePlanerComponent,
    data: { title: 'Planer', permission: 'Permissions.SchedulerItems.Search'},
    canActivate: [AuthGuard],
    resolve: {
      scheduler: SchedulerResolver,
      employees: EmployeesResolver,
      ressources: SchedulerResourcesResolver,
      holidays: CurrentDayHolidayResolver
    },
  },
  {
    path: "contacts",
    component: ContactsComponent,
    data: { title: 'Kontakte', permission: 'Permissions.Contacts.Search'},
    canActivate: [AuthGuard],
    resolve: {
      contacts: ContactsResolver
    },
  },
  {
    path: "assignments",
    component: AssignmentsComponent,
    data: { title: 'Aufträge', permission: 'Permissions.Assignments.Search'},
    canActivate: [AuthGuard],
    resolve: {
      assignments: AssignmentsResolver
    },
  },
  {
    path: "assignment",
    component: AssignmentComponent,
    data: { permission: 'Permissions.Assignments.Create'},
    canActivate: [AuthGuard],
    resolve: {
      currentEmployee: CurrentEmployeeResolver,
    }
  },
  {
    path: "assignment/:id",
    component: AssignmentComponent,
    data: {permission: 'Permissions.Assignments.View'},
    canActivate: [AuthGuard],
    resolve: {
      assignment: AssignmentResolver
    },
  },
  {
    path: "contact",
    component: ContactComponent,
    data: {permission: 'Permissions.Contacts.Create'},
    canActivate: [AuthGuard],
  },
  {
    path: "contact/:id",
    component: ContactComponent,
    data: {permission: 'Permissions.Contacts.View'},
    canActivate: [AuthGuard],
    resolve: {
      contact: ContactResolver
    },
  },
  {
    path: "time-entries",
    component: TimeEntriesComponent,
    // Basic permission on purpose: every employee reaches their own "Meine Zeiterfassung" tab here.
    // Inside the component the "all reports" tab takes TimeEntries.Frontend plus somebody within
    // reach (subordinates, or everyone with SearchAll); the evaluation tab is open to all.
    data: { title: 'Zeiterfassung', permission: 'Permissions.TimeEntries.StartStopTimeEntry'},
    canActivate: [AuthGuard],
    resolve: {
      timeEntryCategories: TimeEntryCategoriesResolver,
    },
  },
  {
    path: "absences",
    component: AbsencesComponent,
    // Every authenticated employee reaches this page: own absences plus the all-absences calendar
    // (redacted outside the viewer's reach). Only the list view in the "all absences" tab is gated,
    // inside the component, by the supervisor structure or Permissions.AbsenceGroups.SearchAll.
    data: { title: 'Abwesenheiten', permission: 'Permissions.AbsenceGroups.Search'},
    canActivate: [AuthGuard],
  },
  {
    path: "expenses",
    component: ExpensesComponent,
    data: { title: 'Spesen', permission: 'Permissions.Expenses.View'},
    canActivate: [AuthGuard],
  },
  {
    path: "settings",
    component: SettingComponent,
    // No own permission: the settings hub opens as soon as any child page below is
    // permitted (see hasRouteAccess); the hub itself only renders the permitted buttons.
    data: { title: 'Einstellungen' },
    canActivate: [AuthGuard],
    // Child titles are transloco keys (translated by the settings hub) - the same key as
    // the page's own heading, so button and heading never drift apart.
    children: [
      {
        path: "employees",
        component: EmployeesComponent,
        // Deliberately View and not Search: Search is a basic permission every employee
        // holds for the dropdown filters, and would open this admin page to everyone.
        data: { title: 'settings.employees', permission: 'Permissions.Employees.Manage'},
        canActivate: [AuthGuard],
        resolve: {
          employees: EmployeesResolver
        }
      },
      {
        path: "employee/:id",
        component: EmployeeComponent,
        data: {  permission: 'Permissions.Employees.Update'},
        canActivate: [AuthGuard],
        resolve: {
          employee: EmployeeResolver,
          workTimes: EmployeeWorkTimeResolver
        }
      },
      {
        path: "employee",
        component: EmployeeComponent,
        data: {  permission: 'Permissions.Employees.Create'},
        canActivate: [AuthGuard]
      },
      {
        path: "department",
        component: DepartmentComponent,
        data: { title: 'settings.departments', permission: 'Permissions.Departments.Manage'},
        canActivate: [AuthGuard],
        resolve: {
          departements: DepartmentResolver
        },
      },
      {
        path: "roles",
        component: EmployeeRolesComponent,
        data: { title: 'settings.employeeRoles', permission: 'Permissions.EmployeeRoles.Manage'},
        canActivate: [AuthGuard],
        resolve: {
          employeeRoles: EmployeeRoleResolver
        },
      },
      {
        path: "ressource-categories",
        component: RessourceCategoryComponent,
        data: { title: 'settings.ressourceCategories', permission: 'Permissions.RessourceCategories.Manage'},
        canActivate: [AuthGuard],
        resolve: {
          ressourceCategories: RessourceCategoryResolver
        },
      },
      {
        path: "ressources",
        component: RessourceComponent,
        data: { title: 'settings.ressources', permission: 'Permissions.Ressources.Create'},
        canActivate: [AuthGuard],
        resolve: {
          ressources: RessourceResolver
        },
      },
      {
        path: "time-entry-categories",
        component: TimeEntryCategoryComponent,
        data: { title: 'settings.activities', permission: 'Permissions.TimeEntryCategories.Create'},
        canActivate: [AuthGuard],
        resolve: {
          timeEntryCategories: TimeEntryCategoriesResolver
        },
      },
      {
        path: "assignment-types",
        component: AssignmentTypeComponent,
        data: { title: 'settings.assignmentTypes', permission: 'Permissions.AssignmentTypes.View'},
        canActivate: [AuthGuard],
        resolve: {
          assignmentTypes: AssignmentTypesResolver
        },
      },
      {
        path: "expense-types",
        component: ExpenseTypesComponent,
        data: { title: 'expenseTypes.title', permission: 'Permissions.ExpenseTypes.Create'},
        canActivate: [AuthGuard],
      },
      {
        path: "flex-time-corrections",
        component: FlexTimeCorrectionGridComponent,
        data: { title: 'flexTimeCorrection.title', permission: 'Permissions.FlexTimeCorrections.Create'},
        canActivate: [AuthGuard],
        resolve: {
          flexTimeCorrections: FlexTimeCorrectionsResolver
        },
      },
      {
        path: "assignment-services",
        component: ServicesGridComponent,
        data: { title: 'assignment.service.title', permission: 'Permissions.AssignmentServices.Create'},
        canActivate: [AuthGuard],
        resolve: {
          services: ServicesResolver
        },
      },
      {
        path: "holidays",
        component: HolidayGridComponent,
        data: { title: 'holiday.title', permission: 'Permissions.Holidays.Create'},
        canActivate: [AuthGuard],
        resolve: {
          holidays: HolidayResolver
        }
      },
      {
        path: "permission-roles",
        component: PermissionRolesComponent,
        data: { title: 'settings.permissionRoles', permission: 'Permissions.Roles.Create'},
        canActivate: [AuthGuard],
        resolve: {
          roles: PremissionRolesResolver
        },
      },
      {
        path: "fiscal-years",
        component: FiscalYearGridComponent,
        data: { title: 'fiscal-year.title', permission: 'Permissions.FiscalYears.Create'},
        canActivate: [AuthGuard]
      },
      {
        path: "time-supplements",
        component: TimeSupplementsComponent,
        // Only offered while the tenant option is on - switched off, the supplements are not applied either.
        data: { title: 'timeSupplements.title', permission: 'Permissions.TimeSupplements.Create', tenantSetting: 'hasTimeSupplements'},
        canActivate: [AuthGuard, TenantSettingGuard]
      },
      {
        path: "tenant-settings",
        component: TenantSettingsComponent,
        data: { title: 'tenant-settings.title', permission: 'Permissions.TenantSettings.Update'},
        canActivate: [AuthGuard]
      }   
    ]
  },
  {
    path: "tenant",
    component: TenantComponent,
    data: { title: 'Mandanten', permission: 'Permissions.Tenants.Create'},
    canActivate: [AuthGuard],
    resolve: {
      tenants: TenantResolver
    },
  },
  {
    path: "profile",
    component: ProfileComponent,
    data: { title: 'Profil', permission:'Permissions.Personal.View'},
    canActivate: [AuthGuard],
  },
  {
    path: "auth",
    component: LoginComponent,
  },
  {
    path: "403",
    component: PageForbiddenComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "404",
    component: PageNotFoundComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "**",
    component: PageNotFoundComponent,
    canActivate: [AuthGuard],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
